<template>
  <div>
    <!-- Text/buttons under the map -->
    <div class="row m-b-sm">
      <div class="col-md-6 text-md-left text-center">
        Total results found (adjusters with coordinates only): <strong>{{ markersCount }}</strong>
      </div>
      <div class="col-md-6 text-md-right text-center">
        <button class="btn btn-sm btn-default" @click="showMapSettingsModal">
          <i class="fa fa-cog"></i> Map view settings
        </button>
      </div>
    </div>

    <!-- The map -->
    <div id="map" ref="map" :style="mapStyle"/>

    <!-- Map settings modal -->
    <modal id="mapSettingsModal" @hidden="onSettingsModalHide">

      <template v-slot:header>
        <i class="fa fa-cog modal-icon"></i>
        <h4 class="modal-title">Map View Settings</h4>
      </template>

      <!-- Clustering -->
      <div class="form-group row m-t-md">
        <div class="col-sm-10 offset-sm-2">
          <div class="form-check">
            <input class="form-check-input" type="checkbox" v-model="settings.clustering" id="clusteringCheckbox">
            <label class="form-check-label" for="clusteringCheckbox">
              Join markers in clusters to improve the map performance.
            </label>
          </div>
          <div class="small text-secondary m-t-sm alert alert-warning">
            <i class="fa fa-info-circle"></i> It is recommended to activate this option to avoid slow loading of the map.
            Please note that if you want to show a large number of adjusters (over 1000),
            disabling this option may cause your web browser to freeze.
          </div>
        </div>
      </div>

      <!-- Infowindows -->
      <div class="form-group row m-t-md">
        <div class="col-sm-10 offset-sm-2">
          <div class="form-check">
            <input class="form-check-input" type="checkbox" v-model="settings.infowindows" id="infowindowsCheckbox">
            <label class="form-check-label" for="infowindowsCheckbox">
              Show adjuster information window when the marker is clicked.
            </label>
          </div>
          <div class="small text-secondary m-t-sm alert alert-warning">
            <i class="fa fa-info-circle"></i> This option allows the adjuster information window to be displayed
            when the marker is clicked. If you only want to see the markers, it is advisable to disable
            the information windows. This increases the loading speed of the map.
          </div>
        </div>
      </div>

      <template #footer>
        <button type="button" class="btn btn-default" @click="hideMapSettingsModal">
          <i class="fa fa-fw fa-close"></i> Cancel
        </button>
        <button class="btn btn-primary" @click="applyMapSettings">
          <i class="fa fa-fw fa-save"></i> Save
        </button>
      </template>

    </modal>

  </div>
</template>

<script>
  /**
   * This shows a google map with a list of adjusters.
   */

  import gmapsInit from '../../utils/gmaps'
  import {MarkerClusterer, SuperClusterAlgorithm} from "@googlemaps/markerclusterer";

  export default {
    /**
     * Input properties.
     */
    props: {

      // Google Maps Api key.
      apiKey: {
        type: String,
        default: '',
      },

      // Google Maps Api base url.
      apiUrl: {
        type: String,
        default: 'https://maps.googleapis.com/maps/api/',
      },

      // Map center latitude.
      latitude: {
        type: String,
        default: '',
      },

      // Map center longitude.
      longitude: {
        type: String,
        default: '',
      },

      // Zoom.
      zoom: {
        type: Number,
        default: 4,
      },

      // Markers. Array of objects. Every object should contain the following keys:
      // - lt - latitude
      // - ln - longitude
      // - id - adjuster id
      markerSources: {
        type: Array,
        default: function () { return [] }
      },

      // Threshold of marker amount after which the markers will be grouped (clustered).
      clusteringThreshold: {
        type: Number,
        default: 100,
      },

      // Map width css.
      mapWidth: {
        type: String,
        default: '100%',
      },

      // Map height css.
      mapHeight: {
        type: String,
        default: '50vh',
      },

      // Use marker clustering.
      settingsClustering: {
        type: Boolean,
        default: true
      },

      // Show infowindows when marker is clicked.
      settingsInfowindows: {
        type: Boolean,
        default: true
      },

    },

    /**
     * Variables (models).
     */
    data () {
      return {
        // Google library object.
        google: null,

        // Map object.
        map: null,

        // Settings form fields.
        settings: {
          // Use marker clustering.
          clustering: true,
          // Show infowindows when marker is clicked.
          infowindows: true
        },

        // Settings form fields on the settings form.
        formSettings: {},
      }
    },

    /**
     * Functions (methods).
     */
    methods: {

      async initMap(center = null, zoom = null) {
        // Request needed libraries.
        const { Map, InfoWindow } = await this.google.maps.importLibrary("maps");
        const { AdvancedMarkerElement } = await this.google.maps.importLibrary(
          "marker",
        );
        const mapCenter = new this.google.maps.LatLng(this.latitude, this.longitude);
        const map = new this.google.maps.Map(document.getElementById("map"), {
          center: center ? center : mapCenter,
          zoom: zoom ? zoom : this.zoom,
          mapId: 'ADJUSTER_MAP',
        });

        const infoWindow = new this.google.maps.InfoWindow({
          content: "",
          disableAutoPan: true,
        });

        // If no markers present, return here.
        if (this.markerSources.length === 0) {
          this.map = map;
          return;
        }

        // No need for clustering by default.
        let needClusters = false;

        // If clustering enabled in map view settings.
        if (this.settings.clustering) {
          // If marker amount is after the clustering threshold, markers should be grouped (joined in clusters).
          if (this.markerSources.length > this.clusteringThreshold) {
            needClusters = true;
          }
        }

        // Generate the markers.
        const markers = this.markerSources.map((markerData, i) => {
          const position = new this.google.maps.LatLng(markerData.lt, markerData.ln);
          const marker = new this.google.maps.marker.AdvancedMarkerElement({position});
          if (!needClusters) marker.setMap(map);

          if (this.settings.infowindows) {
            marker.addListener("click", () => {
              this.loadMarkerInfo(infoWindow, markerData);
              infoWindow.open(map, marker);
            });
          }

          return marker;
        });

        // Make the clustering if needed.
        if (needClusters) {
          const algorithm = new SuperClusterAlgorithm({ maxZoom: 8 });
          new MarkerClusterer({ markers, map, algorithm });
        }

        this.map = map;
      },

      /**
       * Load marker data to infowindow on marker click.
       */
      loadMarkerInfo(infoWindow, markerData) {
        infoWindow.setContent('Loading...');
        axios.get('/adjusters/' + markerData.id + '/info-short')
            .then(response =>{
              infoWindow.setContent(response.data);
            })
            .catch(error =>{
              infoWindow.setContent(
                  '<div class="alert alert-danger m-b-none">' +
                  this.generateErrorMessage(
                  error,
                  'Unable to load adjuster\'s data.') +
                  '</div>'
              );
            });
      },

      /**
       * Handle axios errors
       *
       * @param errorObj
       * @param defaultMessage
       */
      generateErrorMessage(errorObj, defaultMessage) {
        if (errorObj.response.status === 422) {
          return defaultMessage + ' ' + (errorObj.response.data.errors ? errorObj.response.data.errors.join('; ') : '');
        } else if (errorObj.response.status === 404) {
          return defaultMessage + ' ' + 'The object not found.';
        } else {
          return defaultMessage;
        }
      },

      /**
       * Apply map settings.
       */
      applyMapSettings() {
        // Update displayed form settings.
        this.formSettings = Object.assign({}, this.formSettings, this.settings);

        // Close the modal.
        this.hideMapSettingsModal();

        // Save map state.
        let center = this.map.getCenter();
        let zoom = this.map.getZoom();

        // Reload map and markers.
        this.initMap(center, zoom);

        // Save settings.
        axios.post('/adjusters/update-map-view-mode-settings', this.settings);
      },

      onSettingsModalHide() {
        this.settings = Object.assign({}, this.settings, this.formSettings);
      },

      /**
       * Show map view settings modal.
       */
      showMapSettingsModal() {
        this.$modal.show('mapSettingsModal');
      },

      /**
       * Hide map view settings modal.
       */
      hideMapSettingsModal() {
        this.$modal.hide('mapSettingsModal');
      },

    },

    // Computed properties.
    computed: {

      /**
       * Map css.
       */
      mapStyle: function () {
        return 'width: ' + this.mapWidth + '; height: ' + this.mapHeight;
      },

      /**
       * Amount of markers.
       */
      markersCount: function () {
        return this.markerSources.length;
      }

    },

    /**
     * Initialize component on mount.
     */
    async mounted() {
      this.settings.clustering = this.settingsClustering;
      this.settings.infowindows = this.settingsInfowindows;
      this.formSettings = Object.assign({}, this.formSettings, this.settings);
      this.google = await gmapsInit(this.apiUrl, this.apiKey);
      this.initMap();
    }
  }
</script>

<style scoped>
</style>
